import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { CartAddEvent } from '@theme/events';
import { formatMoney } from '@theme/money-formatting';

/**
 * dm-bundle — one composite bundle card.
 *
 * A SUBSCRIBER of the page's buy form — <dm-personalizer> on the personalized template,
 * <dm-standard-form> on the standard one. Both publish the same `snapshot` shape, so the
 * card reads readiness and the main line's price from whichever is present.
 *
 * On the personalized template every bundled item inherits the step-1 pet name and
 * colours (contract §Bundles, decision A), with an in-place Change name override per
 * line, and the CTA stays disabled until the configurator is complete — before that
 * there is nothing to inherit. On the standard template there is no name or colour to
 * inherit, so the snapshot's empty strings simply write no personalization properties
 * and the CTA is ready as soon as a variant resolves.
 *
 * A line whose product is the CURRENT product uses the buy form's selected variant and
 * colours; other lines pin the variant rendered by Liquid and take the main item's fabric. A
 * product that lists fabrics and not this one cannot be made to match, so the set waits for
 * another fabric and says why. One items[] POST.
 */
const NON_LETTERS = /[^a-zA-Z]/g;
const ADDED_HOLD_MS = 4000;

/**
 * The same key Liquid's handleize gives a fabric label (dm-fabric-map).
 * @param {string} label
 */
function fabricKey(label) {
  return label.toLowerCase().trim().replace(/['"]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
}

class DmBundle extends Component {
  #busy = false;
  #added = false;
  #addedTimer = 0;
  #ctaLabel = '';
  #hintLabel = '';

  #onChange = () => this.#render();

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener('dm:state', this.#onChange);
    document.addEventListener('dm:gating-change', this.#onChange);

    this.#ctaLabel = this.querySelector('[data-dm-bundle-cta]')?.textContent?.trim() || '';
    this.#hintLabel = this.querySelector('[data-dm-bundle-hint]')?.textContent?.trim() || '';

    this.querySelector('[data-dm-bundle-cta]')?.addEventListener('click', () => {
      this.#add().catch((error) => {
        console.error('[dm-bundle] Add failed.', error);
        this.#error('Unable to add the set right now. Your choices are saved.');
        this.#busy = false;
        this.#render();
      });
    });

    for (const line of this.#lines()) {
      const rename = line.querySelector('[data-dm-bundle-rename]');
      const input = /** @type {HTMLInputElement | null} */ (line.querySelector('[data-dm-bundle-name]'));

      rename?.addEventListener('click', () => {
        if (!input) return;
        const showing = !input.hidden;
        input.hidden = showing;
        if (!showing) {
          input.value = /** @type {any} */ (line).__nameOverride ?? this.#inheritedName();
          input.focus();
        }
      });

      input?.addEventListener('input', () => {
        const sanitized = input.value.replace(NON_LETTERS, '').toUpperCase();
        if (input.value !== sanitized) input.value = sanitized;
        /** @type {any} */ (line).__nameOverride = sanitized || null;
        this.#render();
      });
    }

    this.#render();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('dm:state', this.#onChange);
    document.removeEventListener('dm:gating-change', this.#onChange);
    window.clearTimeout(this.#addedTimer);
  }

  /** @returns {HTMLElement[]} */
  #lines() {
    return Array.from(this.querySelectorAll('[data-dm-bundle-line]'));
  }

  /** The buy form on whichever product template is rendering. */
  #controller() {
    const element = /** @type {any} */ (document.querySelector('dm-personalizer, dm-standard-form'));
    return element && 'snapshot' in element ? element : null;
  }

  #snapshot() {
    return this.#controller()?.snapshot || null;
  }

  #inheritedName() {
    return this.#snapshot()?.petName || '';
  }

  #render() {
    const snap = this.#snapshot();
    const cta = /** @type {HTMLButtonElement | null} */ (this.querySelector('[data-dm-bundle-cta]'));
    const hint = this.querySelector('[data-dm-bundle-hint]');
    const unmatched = this.#lineWithoutFabric(snap);
    // A Plain bow has no name for the set to inherit, so it never posts a blank personalization.
    const ready = Boolean(snap?.canSubmit) && !snap?.noName && !unmatched;

    this.dataset.state = this.#added ? 'added' : this.#busy ? 'loading' : ready ? 'ready' : 'waiting';

    if (cta) {
      cta.disabled = !ready || this.#busy;
      cta.setAttribute('aria-busy', String(this.#busy));
      if (this.#added) cta.textContent = '✓ Bundle Added';
    }
    if (hint instanceof HTMLElement) {
      hint.hidden = ready;
      hint.textContent = unmatched
        ? `${unmatched} does not come in ${snap.toyColorLabel}. Choose another color to add the set.`
        : this.#hintLabel;
    }

    // Per-line inherited-name labels + live total (the main line's price follows the
    // configurator's selected size).
    let total = 0;
    for (const line of this.#lines()) {
      const isMain = line.dataset.isMain === 'true';
      total += isMain && snap?.variantPrice ? snap.variantPrice : Number(line.dataset.price) || 0;

      const label = line.querySelector('[data-dm-bundle-name-label]');
      if (label instanceof HTMLElement && line.dataset.personalizable === 'true') {
        const name = /** @type {any} */ (line).__nameOverride ?? this.#inheritedName();
        label.textContent = name ? `Embroidered “${name}”` : '';
      }
    }

    const totalElement = this.querySelector('[data-dm-bundle-total]');
    if (totalElement instanceof HTMLElement && total > 0) {
      totalElement.textContent = this.#money(total);
    }
  }

  async #add() {
    const controller = this.#controller();
    const snap = this.#snapshot();
    if (!controller || !snap?.canSubmit || snap.noName || this.#lineWithoutFabric(snap) || this.#busy) return;

    this.#busy = true;
    this.#error('');
    this.#render();

    const items = this.#lines().map((line) => {
      const isMain = line.dataset.isMain === 'true';
      const personalizable = line.dataset.personalizable === 'true';
      const name = /** @type {any} */ (line).__nameOverride ?? snap.petName;

      /** @type {Record<string, string>} */
      const properties = {
        _dm_bundle: this.dataset.bundleTitle || 'Bundle',
        _dm_template: snap.mode === 'standard' ? 'dm-standard' : 'dm-personalized',
      };

      // On the standard template there is no name to inherit, so a personalizable
      // product bundled there must not be sent with an empty "Pet Name" — that would
      // put a blank personalization line in the cart and into the order.
      if (personalizable && name) {
        properties['Pet Name'] = name;
        properties._wroe_personalization_version = '2';
        if (snap.embroideryLabel) properties['Embroidery Color'] = snap.embroideryLabel;
        if (snap.embroideryUrl) properties._wroe_embroidery_color_swatch = snap.embroideryUrl;
      }

      if (isMain) {
        // The same keys as a normal add (dm-personalizer.js #submit).
        const toyKey = snap.fabricMode ? 'Front Fabric' : 'Toy Color';
        const toySwatchKey = snap.fabricMode ? '_wroe_front_fabric_swatch' : '_wroe_toy_color_swatch';
        if (snap.toyColorLabel) properties[toyKey] = snap.toyColorLabel;
        if (snap.toyColorSwatchUrl) properties[toySwatchKey] = snap.toyColorSwatchUrl;
      } else if (personalizable && name && snap.toyColorLabel) {
        // A product with no fabric list takes none. One that lists them has this one, see #lineWithoutFabric.
        const swatch = this.#fabrics(line)[fabricKey(snap.toyColorLabel)];
        if (swatch) {
          properties['Front Fabric'] = snap.toyColorLabel;
          properties._wroe_front_fabric_swatch = swatch;
        }
      }

      return {
        id: Number(isMain ? snap.variantId : line.dataset.variantId),
        quantity: 1,
        properties,
      };
    });

    const addUrl = window.Theme?.routes?.cart_add_url || '/cart/add.js';
    const request = fetchConfig('javascript', { body: JSON.stringify({ items }) });

    try {
      const response = await fetch(addUrl, {
        ...request,
        headers: { ...request.headers, 'Content-Type': 'application/json', Accept: 'application/json' },
      });
      const payload = await response.json();

      if (payload.status) {
        this.#error(payload.description || payload.message || 'Unable to add the set right now.');
        return;
      }

      // The cart icon SETS its bubble to itemCount for this source, so it must be the cart's total.
      const itemCount = await this.#cartItemCount(items.length);

      this.#added = true;
      this.dispatchEvent(
        new CartAddEvent({}, String(items[0]?.id || ''), {
          source: 'dm-bundle',
          itemCount,
        })
      );
      this.dispatchEvent(new CustomEvent('dm:cart-added', { bubbles: true, composed: true }));

      window.clearTimeout(this.#addedTimer);
      this.#addedTimer = window.setTimeout(() => {
        this.#added = false;
        const cta = this.querySelector('[data-dm-bundle-cta]');
        if (cta && this.#ctaLabel) cta.textContent = this.#ctaLabel;
        this.#render();
      }, ADDED_HOLD_MS);
    } finally {
      this.#busy = false;
      this.#render();
    }
  }

  /**
   * The cart's total item count, read from /cart.js. Falls back to the lines just added.
   * @param {number} fallback
   */
  async #cartItemCount(fallback) {
    const root = window.Shopify?.routes?.root || '/';
    const cart = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } })
      .then((response) => (response.ok ? response.json() : null))
      .catch(() => null);
    return typeof cart?.item_count === 'number' ? cart.item_count : fallback;
  }

  /**
   * The name of a line that lists fabrics and not the one chosen (owner 2026-11-02), else ''. Its
   * own page requires a fabric, so it must not go to the cart with none.
   * @param {any} snap
   */
  #lineWithoutFabric(snap) {
    if (!snap?.toyColorLabel) return '';
    const key = fabricKey(snap.toyColorLabel);
    const line = this.#lines().find((candidate) => {
      const fabrics = this.#fabrics(candidate);
      return Object.keys(fabrics).length > 0 && !fabrics[key];
    });
    return line?.querySelector('[data-dm-bundle-line-name]')?.textContent?.trim() || '';
  }

  /**
   * The fabrics this line's product offers (dm-fabric-map), key to swatch.
   * @param {HTMLElement} line
   * @returns {Record<string, string>}
   */
  #fabrics(line) {
    try {
      return JSON.parse(line.querySelector('[data-dm-bundle-fabrics]')?.textContent || '{}');
    } catch {
      return {};
    }
  }

  /** @param {string} message */
  #error(message) {
    const element = this.querySelector('[data-dm-bundle-error]');
    if (!(element instanceof HTMLElement)) return;
    element.hidden = !message;
    element.textContent = message;
  }

  /** @param {number} cents */
  #money(cents) {
    // Both buy forms carry the shop's money format, so the bundle total is formatted
    // the same way on either template.
    const form = /** @type {HTMLElement | null} */ (
      document.querySelector('dm-personalizer, dm-standard-form')
    );
    const format = form?.dataset.moneyFormat || '${{amount}}';
    const currency = form?.dataset.currency || 'USD';
    try {
      return formatMoney(cents, format, currency);
    } catch {
      return '';
    }
  }
}

if (!customElements.get('dm-bundle')) {
  customElements.define('dm-bundle', DmBundle);
}
